import InstallTab from '@/components/InstallTab/InstallTab';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PostureManagement } from '@/components/Inventory';
import type { PostureManagementTabGates } from '@/components/Inventory/tabs';
import { SharedSidebar } from '@/components/Sidebar';
import { SidebarNav } from '@/components/SidebarNav/SidebarNav';
import { SidebarNavItem } from '@/components/SidebarNav/components/SidebarNavItem';
import { AgentsActivityNavGroup } from '@/components/SidebarNav/components/AgentsActivityNavGroup';
import { ShadowAINavGroup } from '@/components/SidebarNav/components/ShadowAINavGroup';
import { PoliciesNavGroup } from '@/components/SidebarNav/components/PoliciesNavGroup';
import { AiAppSecNavGroup } from '@/components/SidebarNav/components/AiAppSecNavGroup';
import { policiesPageTitle } from '@/lib/policyPaths';
import { parseAlertToken } from '@kirin/api-client/helpers/alerts';
import { RoleBasedRender } from '@/components/rbac/RoleBasedRender';
import { RoleBasedRoute } from '@/components/rbac/RoleBasedRoute';
import { PageContentRoot } from '@/components/shared/layout/PageContentRoot';
import { Loading } from '@/components/shared/placeholders/Loading';
import { Button } from '@/components/ui/button';
import {
  useOrganization,
  useOrganizations,
} from '@/hooks/api/useOrganizations';
import { InviteAcceptModal } from '@/components/InviteAcceptModal';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Mail } from 'lucide-react';
import { useState } from 'react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useLayout, useLayoutWithSidebar } from '@/hooks/useLayout';
import { UiV2Shell } from '@/components/UiV2/UiV2Shell';
import { useReorderTabsOnSetup } from '@/hooks/useReorderTabsOnSetup';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { roleGroups } from '@/lib/roles';
import { useCanViewAdminContent } from '@/hooks/useRoleCheck';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import DataTableMCPTab from './tabs/DataTableMCPTab';
import { SingleProjectPage } from './tabs/DataTableProjectsTab/SingleProjectPage';
import BrowserAgentSitesPage from './tabs/BrowserAgentSitesTab/BrowserAgentSitesPage';
import { ExtensionScanResultPage } from '@/pages/shared/ExtensionScanResultPage';
import { isExtensionScanResultFromAlerts } from '@/components/grouped-alerts/extension-scan-result-link';
import ExtensionsTab from './tabs/ExtensionsTab';
import PluginsTab from './tabs/PluginsTab';
import { useMembers } from '@/hooks/api/useMembers';
import {
  getSubheaderForTab,
  OrgTab,
  TAB_CONFIG,
  type OrgTabType,
} from './constants';
import { buildOrgDocumentTitle } from './buildOrgDocumentTitle';
import { buildUiV2Nav } from './uiV2Nav';
import { createOrganizationRenderManager } from './createOrganizationRenderManager';
import { Dashboard } from './tabs/Dashboard';

/**
 * Build full path for an organization tab.
 *
 * ⚠️ KIR-2659 deep-link contract: when `tab === OrgTab.Alerts` this
 * returns `/<orgId>/alerts`, which the desktop agent and the IDE
 * extensions hard-code in their summary-banner URL builders
 * (`buildSummaryDeepLinkURL` in
 * extensions/agent/pkg/notifications/coordinator.go and
 * extensions/vscode-core/src/services/notifications/coordinator.ts —
 * search for `org-alt`). The same path is also baked into
 * `buildAlertsDeepLinkUrl` in
 * apps/frontend/src/pages/organization/tabs/DataTableAlertsTab/utils.ts.
 * Changing the `/<orgId>/` shape here or the `'alerts'` string in
 * `OrgTab.Alerts` silently breaks every notification deep-link to the
 * org alerts view AND the in-app deep-link callers (LatestAlertsWidget,
 * DataTableUsersTab CellRenderers). Update those three sites in the
 * same PR.
 */
function buildOrgPath(orgId: string, tab: string): string {
  return `/${orgId}/${tab}`;
}

export default function OrganizationView() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Feature flags
  const { flags, isLoading: isLoadingFlags } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI,
    flagsmithFeatureFlags.ENABLE_ORGANIZATION_DASHBOARD,
    flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED,
    flagsmithFeatureFlags.PROJECTS_INVENTORY,
    flagsmithFeatureFlags.SKILLS_INVENTORY,
    flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY,
    flagsmithFeatureFlags.CAPABILITY_DETECTION,
    flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG,
    flagsmithFeatureFlags.SHADOW_AI_NETWORK_DISCOVERY,
    flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES,
    flagsmithFeatureFlags.BROWSER_AGENT_ACTIVITY,
    flagsmithFeatureFlags.AI_RISK_MAP,
    flagsmithFeatureFlags.UI_V2_SHELL,
  ]);
  const isShadowAiEnabled = flags[flagsmithFeatureFlags.SHADOW_AI] || false;
  // KIR-5055: kill switch for the opt-in shell. Off hides "Try the new UI" and
  // forces Classic even for a browser that already opted in.
  const isUiV2Available = flags[flagsmithFeatureFlags.UI_V2_SHELL] ?? false;
  const isOrganizationDashboardEnabled =
    flags[flagsmithFeatureFlags.ENABLE_ORGANIZATION_DASHBOARD] || false;
  // KIR-5075: AI Risk Map area at the top of the dashboard. Off = not rendered.
  const isAiRiskMapEnabled =
    isOrganizationDashboardEnabled &&
    (flags[flagsmithFeatureFlags.AI_RISK_MAP] ?? false);
  const isHookInventoryEnabled =
    flags[flagsmithFeatureFlags.HOOK_INVENTORY_ENABLED] || false;
  const isProjectsInventoryEnabled =
    flags[flagsmithFeatureFlags.PROJECTS_INVENTORY] || false;
  const isSkillsInventoryEnabled =
    flags[flagsmithFeatureFlags.SKILLS_INVENTORY] || false;
  const isAgentCommandVisibilityEnabled =
    flags[flagsmithFeatureFlags.AGENT_COMMAND_VISIBILITY] || false;
  const isCapabilityDetectionEnabled =
    flags[flagsmithFeatureFlags.CAPABILITY_DETECTION] ?? false;
  // KIR-3925: also gates the Threat Modeling + LLM Vulnerability Discovery
  // pages (the whole AI AppSec section rides this one flag).
  const isSecureAiCodingEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] || false;
  const isNetworkDiscoveryEnabled =
    flags[flagsmithFeatureFlags.SHADOW_AI_NETWORK_DISCOVERY] || false;
  // KIR-4703: gates the user-facing Browser Agent Sites page (nav tab + route).
  // Backend collection is unaffected — only this surface hides when off.
  const isBrowserAgentSitesEnabled =
    flags[flagsmithFeatureFlags.BROWSER_AGENT_ACTIVITY] || false;
  // KIR-3800: gates the Security Groups nav entry, its routes, and the Policies
  // scope selector. Off by default — the backend disables the group APIs too.
  const isSecurityGroupsEnabled =
    flags[flagsmithFeatureFlags.SECURITY_GROUPS_POLICIES] ?? false;
  // KIR-3982: the backend registers the `ai-instruction-policies` group behind
  // `ai_instruction_catalog` (see getAiInstructionPolicies in the policy
  // registry), so the sub-page follows that flag. NOT `ai_rules_policy` — that
  // gates the "AI Instructions Security" policy, which sits in Detection.
  const isAiAgentInstructionsEnabled =
    flags[flagsmithFeatureFlags.AI_INSTRUCTION_CATALOG] || false;

  // Posture Management visibility. canViewAdminContent (owner/manager/support)
  // gates every Inventory sub-tab — Users, Projects, AI Instructions, Hooks —
  // and the /members read. Support reads all of them (KIR-4128).
  const { canViewAdminContent, isLoading: isLoadingRoles } =
    useCanViewAdminContent(organizationId);
  // One gate set feeds both the Inventory tab bar and the UI v2 sidebar, so a
  // tab added to POSTURE_MANAGEMENT_TAB_META shows up in both.
  const inventoryTabGates: PostureManagementTabGates = {
    organizationId,
    canViewAdminContent,
    hookInventoryAllowed: isHookInventoryEnabled,
    projectsInventoryAllowed: isProjectsInventoryEnabled,
    skillsInventoryAllowed: isSkillsInventoryEnabled,
  };
  // Owner/Manager/Support can read /members (backend gates the route to those
  // roles via managerOwnerOrSupport). For members the count is unused, so skip
  // the request to avoid periodic 403 spam.
  const {
    data: members,
    isSuccess: isMembersSuccess,
    isError: isMembersError,
  } = useMembers(organizationId, canViewAdminContent);
  // Block route mount for admins until the members query settles (success or
  // error). Deriving from terminal status — instead of `isLoading` — avoids
  // two race-y states: (1) the render where `enabled` flips false→true, where
  // `isLoading` can be transiently false and would let canMountRoutes turn on
  // with count=0 and mis-gate the Dashboard tab; (2) a failed fetch, where
  // `members` would stay undefined forever and the page would hang on
  // "Loading organization...". Non-admins skip the query, so both flags stay
  // false — the `canViewAdminContent &&` short-circuit makes this `false` and
  // routes mount normally.
  const isLoadingMembers =
    canViewAdminContent && !isMembersSuccess && !isMembersError;

  const {
    tabConfig,
    isLoading: isLoadingAPIKey,
    isSetup,
  } = useReorderTabsOnSetup({
    tabs: TAB_CONFIG,
    selectorKey: OrgTab.Install,
    organizationId,
    installAfterKey: OrgTab.AttackDemonstrations,
  });

  // Register that this page has a sidebar (handles cleanup automatically)
  useLayoutWithSidebar();
  const { uiV2: uiV2OptedIn, setUiV2 } = useLayout();
  const uiV2 = isUiV2Available && uiV2OptedIn;

  // Load organization (hook must be called unconditionally, but is disabled if no organizationId)
  const {
    data: organization,
    isLoading: orgLoading,
    error: orgError,
  } = useOrganization(organizationId);

  // A user who arrives at /:orgId before accepting their invite gets a 403 from
  // useOrganization (not a member yet). Surface an accept prompt instead of the
  // bleak 404 when they have an open invite for this exact org (KIR-3786).
  const { data: userOrganizations = [], isLoading: userOrganizationsLoading } =
    useOrganizations();
  const pendingInviteForThisOrg = userOrganizations.find(
    org => org.id === organizationId && org.isInvited
  );
  const [showOrgInvite, setShowOrgInvite] = useState(false);

  // Determine current tab from URL path (strip trailing slashes first)
  const pathname = location.pathname.replace(/\/+$/, '');

  // Handle nested routes like /{orgId}/alerts/:alertKey/occurrences, and
  // scan-result deep links opened from Alerts (path stays under /inventory/).
  let currentTab: OrgTabType;
  if (
    pathname.includes('/alerts') ||
    isExtensionScanResultFromAlerts(pathname, searchParams)
  ) {
    currentTab = OrgTab.Alerts;
  } else {
    // Skip the first segment (organizationId): an org id that happens to equal
    // a tab slug (e.g. "settings") must not be mistaken for the tab. The tab
    // slot is the segment directly under the org id; match it explicitly first
    // so a future nested sub-slug (e.g.
    // /{orgId}/policies/extension-security-catalog) can never flip tab
    // detection. Fall back to scanning the remaining segments, then Install.
    const segments = pathname.split('/').filter(Boolean).slice(1);
    const tabValues = Object.values(OrgTab);
    currentTab =
      tabValues.find(tab => tab === segments[0]) ??
      tabValues.find(tab => segments.includes(tab)) ??
      OrgTab.Install;
  }

  /**
   * Tabs manager
   */
  const {
    canMountRoutes,
    isTabVisible,
    getDefaultTab,
    isLoading: tabsAreLoading,
    isError: tabsAreError,
  } = createOrganizationRenderManager({
    flags: {
      shadowAiEnabled: isShadowAiEnabled,
      organizationDashboardEnabled: isOrganizationDashboardEnabled,
      agentCommandVisibilityEnabled: isAgentCommandVisibilityEnabled,
      secureAiCodingEnabled: isSecureAiCodingEnabled,
      securityGroupsEnabled: isSecurityGroupsEnabled,
    },
    loadingStates: {
      isLoadingOrganization: orgLoading,
      isLoadingApiKey: isLoadingAPIKey,
      isLoadingMembers: isLoadingMembers,
      isLoadingRoles,
    },
    errorStates: {
      // Treat both fetch errors and "no organization data" as error states
      // to restore the previous behavior where orgError || !organization
      // showed the 404 page and prevented routes from mounting.
      isOrganizationError: orgError ? true : !orgLoading && !organization,
    },
    orgMeta: {
      organizationId: organizationId || '',
      // KIR-3908: count only real accounts. Discovered/Unattributed rows are
      // ingest-created (a single Cowork hook scan mints one), so counting them
      // would trip the `< 2` Dashboard-tab gate for a genuinely single-person org.
      organizationMembersCount:
        members?.filter(m => !m.userType || m.userType === 'full').length || 0,
      isSetup: isSetup,
      canViewAdminContent,
    },
    tabsConfig: tabConfig,
  });

  // to ensure that there is no invalid tab usage
  const safeCurrentTTab = isTabVisible(currentTab)
    ? currentTab
    : getDefaultTab();
  // Shadow AI and Policies are nav groups; reflect the sub-page in the title
  // ("Policies / Detection Policies" etc.). Catalog sub-routes keep "Policies".
  // Agents Activity is also a nav group: the default route is "Packages & Repos"
  // and the /browser-agent-sites sub-route is its own page (KIR-4703).
  const isBrowserAgentSites =
    safeCurrentTTab === OrgTab.AgentsActivity &&
    pathname.endsWith('/browser-agent-sites');
  const currentPageTitle =
    safeCurrentTTab === OrgTab.ShadowAI
      ? pathname.endsWith('/network-discovery')
        ? 'Shadow AI / Network Discovery'
        : 'Shadow AI / Tools'
      : safeCurrentTTab === OrgTab.Policies
        ? (policiesPageTitle(pathname) ?? TAB_CONFIG[safeCurrentTTab].label)
        : isBrowserAgentSites
          ? 'Browser Agent Sites'
          : TAB_CONFIG[safeCurrentTTab].label;
  const currentPageIcon = TAB_CONFIG[safeCurrentTTab].icon;
  // KIR-2788: the Alerts tab doubles as the notification landing at
  // /:org/alerts/:token — a scoped view of the exact alert(s) that fired, not
  // the org-wide list, so the "Review security alerts across {org}" subheader no
  // longer fits there.
  const alertsTokenPrefix = `/${organizationId ?? ''}/alerts/`;
  const isNotificationLanding =
    safeCurrentTTab === OrgTab.Alerts &&
    pathname.startsWith(alertsTokenPrefix) &&
    // parseAlertToken returns undefined (not null) for a non-token sub-path
    // (e.g. `{key}/occurrences`), so compare against undefined — `!== null`
    // would be true for every /alerts/* sub-route.
    parseAlertToken(pathname.slice(alertsTokenPrefix.length)) !== undefined;
  const currentPageSubheader = isNotificationLanding
    ? 'The alert(s) from one notification'
    : isBrowserAgentSites
      ? `Sites reached by browser AI agents across ${organization?.name ?? 'your organization'}`
      : getSubheaderForTab(safeCurrentTTab, organization?.name);

  // Set browser tab title (with organization name if available).
  // Page name comes first so it stays visible when the tab caption is truncated;
  // the hook will append " - Kirin" → "[Page] - [Org] - Kirin".
  const documentTitle = buildOrgDocumentTitle(
    currentPageTitle,
    organization?.name
  );
  useDocumentTitle(documentTitle);

  // Redirect to dashboard if organizationId is missing
  // this should never happen since the component is rendering the route `/:organizationId`
  // failsafe if it gets used somewhere else
  if (!organizationId) {
    return <Navigate to="/dashboard" replace />;
  }

  // Page content is identical under both shells.
  const content = (
    <>
      {/* Loading state */}
      {tabsAreLoading && <Loading label="Loading organization..." />}

      {/* Don't decide 404 vs invite-prompt until the org list settles —
          otherwise the 404 flashes before a pending invite for this org
          loads (KIR-3786). */}
      {tabsAreError && userOrganizationsLoading && (
        <Loading label="Loading organization..." />
      )}

      {/* Error state: pending-invite prompt for invitees, else 404 */}
      {tabsAreError &&
        !userOrganizationsLoading &&
        (pendingInviteForThisOrg ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <HighlightBox
              variant="info"
              radius="lg"
              padding="lg"
              className="max-w-md text-center"
            >
              <Mail className="mx-auto mb-3 h-6 w-6 text-muted-foreground" />
              <h2 className="mb-2 text-lg font-semibold">
                You've been invited to {pendingInviteForThisOrg.name}
              </h2>
              <p className="mb-4 text-sm text-muted-foreground">
                Accept the invitation to join this organization and access its
                dashboard.
              </p>
              <Button onClick={() => setShowOrgInvite(true)}>
                Accept invitation
              </Button>
            </HighlightBox>
            <InviteAcceptModal
              open={showOrgInvite}
              onOpenChange={setShowOrgInvite}
              organizationId={organizationId}
              organizationName={pendingInviteForThisOrg.name}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">404</h1>
              <p className="text-muted-foreground mb-4">
                Organization not found or you don't have access
              </p>
              <Button onClick={() => (window.location.href = '/dashboard')}>
                Go to Dashboard
              </Button>
            </div>
          </div>
        ))}

      {/* Routes - only when loaded successfully */}
      {canMountRoutes && (
        <Routes>
          {/* /:organizationId → redirect to install tab */}
          <Route
            index
            element={
              <Navigate
                to={buildOrgPath(organizationId, getDefaultTab())}
                replace
              />
            }
          />

          {/* Backwards-compat redirect: /posture-management → /inventory */}
          <Route
            path="posture-management"
            element={
              <Navigate
                to={buildOrgPath(organizationId, 'inventory')}
                replace
              />
            }
          />

          {/* /:organizationId/* - tab routes (generated from config) */}
          {Object.values(OrgTab).map(tab => {
            const isVisibleTab = isTabVisible(tab);
            if (!isVisibleTab) {
              return null;
            }
            const config = TAB_CONFIG[tab];
            const Component = config.component;

            const isPostureManagementTab = tab === OrgTab.PostureManagement;

            // Gate PostureManagement rendering while roles OR flags are loading to prevent race conditions:
            // 1. Tabs defaultValue computed with wrong canViewAdminContent=false during role loading
            // 2. AI Instructions tab guard failing when flags load after roles (causing URL rewrite)
            const shouldWaitForRoles =
              isPostureManagementTab && (isLoadingRoles || isLoadingFlags);

            const element =
              tab === OrgTab.Install ? (
                <InstallTab organizationId={organizationId} />
              ) : shouldWaitForRoles ? null : isPostureManagementTab ? (
                <PostureManagement
                  {...inventoryTabGates}
                  MCPTabSlot={<DataTableMCPTab />}
                  ExtensionsTabSlot={<ExtensionsTab />}
                  PluginsTabSlot={<PluginsTab />}
                />
              ) : tab === OrgTab.Dashboard ? (
                <Dashboard
                  isShadowAIEnabled={isShadowAiEnabled}
                  isAiRiskMapEnabled={isAiRiskMapEnabled}
                />
              ) : (
                <Component />
              );

            return (
              // ⚠️ KIR-2659 deep-link contract: the org alerts
              // subroute `/:organizationId/alerts` is referenced
              // verbatim by the agent + IDE extension when they
              // build summary-banner "View Details" deep links
              // (extensions/agent/pkg/notifications/coordinator.go
              // and .../vscode-core/.../coordinator.ts —
              // buildSummaryDeepLinkURL hits this path when an
              // org context is known). Renaming the `'alerts/*'`
              // path segment silently breaks every notification
              // deep-link to the org alerts view; update the URL
              // builders in the same PR.
              <Route
                key={tab}
                path={
                  tab === OrgTab.Alerts
                    ? 'alerts/*'
                    : tab === OrgTab.Policies
                      ? 'policies/*'
                      : tab === OrgTab.ShadowAI
                        ? 'shadow-ai/*'
                        : tab === OrgTab.SecurityGroups
                          ? 'security-groups/*'
                          : tab
                }
                element={
                  <RoleBasedRoute
                    roles={config.roles}
                    redirectTo={`/${organizationId}`}
                  >
                    {element}
                  </RoleBasedRoute>
                }
              />
            );
          })}

          {/* Single-project page (KIR-3356 / KIR-3517). Explicit route —
              the inventory tab itself is query-param based (?tab=), so the
              project detail lives at its own path. Gated by `projects_inventory`
              alone so the KIR-3356 project detail stays reachable; the page
              itself gates its security-coverage view behind
              `ai_instruction_catalog` and falls back to plain detail when that
              flag is off. Same roles as the Projects tab so support can drill
              into a row it can already see (KIR-4128). */}
          <Route
            path="inventory/projects/:projectSlug"
            element={
              isLoadingFlags ? null : isProjectsInventoryEnabled ? (
                <RoleBasedRoute
                  roles={{
                    param: 'organizationId',
                    anyOf: [...roleGroups.managerOwnerOrSupport],
                  }}
                  redirectTo={`/${organizationId}`}
                >
                  <SingleProjectPage />
                </RoleBasedRoute>
              ) : (
                <Navigate
                  to={buildOrgPath(organizationId, 'inventory')}
                  replace
                />
              )
            }
          />

          {/* KIR-4703/4704: Browser Agent Sites, a child of Agents
              Activity. Explicit route (the agent-activity tab route is
              exact, not a wildcard). Reads the real view API; enforcement +
              approval persist via the decision mutation. key={organizationId}
              remounts the page on org switch so filters/sort/expanded reset. */}
          <Route
            path="agent-activity/browser-agent-sites"
            element={
              isLoadingFlags ? null : isBrowserAgentSitesEnabled ? (
                <RoleBasedRoute
                  roles={{
                    param: 'organizationId',
                    anyOf: [...roleGroups.managerOwnerOrSupport],
                  }}
                  redirectTo={`/${organizationId}`}
                >
                  <BrowserAgentSitesPage key={organizationId} />
                </RoleBasedRoute>
              ) : (
                <Navigate
                  to={buildOrgPath(organizationId, 'inventory')}
                  replace
                />
              )
            }
          />

          <Route
            path="inventory/extensions/:extensionId/scan-result"
            element={
              isLoadingFlags ? null : isCapabilityDetectionEnabled ? (
                <ExtensionScanResultPage organizationId={organizationId} />
              ) : (
                <Navigate
                  to={buildOrgPath(organizationId, 'inventory')}
                  replace
                />
              )
            }
          />

          {/* Catch-all - redirect to organization root */}
          <Route
            path="*"
            element={<Navigate to={`/${organizationId}`} replace />}
          />
        </Routes>
      )}
    </>
  );

  if (uiV2) {
    return (
      <UiV2Shell
        sections={
          canMountRoutes
            ? buildUiV2Nav({
                organizationId,
                pathname,
                tabParam: searchParams.get('tab'),
                currentTab: safeCurrentTTab,
                tabConfig,
                isTabVisible,
                canViewAdminContent,
                inventoryTabGates,
                networkDiscoveryEnabled: isNetworkDiscoveryEnabled,
                browserAgentSitesEnabled: isBrowserAgentSitesEnabled,
                aiAgentInstructionsEnabled: isAiAgentInstructionsEnabled,
              })
            : []
        }
        title={currentPageTitle}
        subheader={currentPageSubheader}
        homeTo={
          isTabVisible(OrgTab.Dashboard)
            ? buildOrgPath(organizationId, OrgTab.Dashboard)
            : `/${organizationId}`
        }
        alertsTo={buildOrgPath(organizationId, OrgTab.Alerts)}
        onSwitchToClassic={() => setUiV2(false)}
      >
        {content}
      </UiV2Shell>
    );
  }

  return (
    <>
      <SharedSidebar>
        {canMountRoutes && (
          <SidebarNav>
            {Object.keys(tabConfig).map(tab => {
              const isVisibleTab = isTabVisible(tab as OrgTabType);

              // KIR-3925: the AI AppSec section renders at the SecureAiCoding
              // slot; its two extra tabs never render as standalone items.
              // All three ride the ai_instruction_catalog flag.
              if (
                tab === OrgTab.ThreatModeling ||
                tab === OrgTab.LlmVulnDiscovery
              ) {
                return null;
              }
              if (!isVisibleTab) {
                return null;
              }
              if (tab === OrgTab.SecureAiCoding) {
                const tabConfigEntry = tabConfig[tab as OrgTabType];
                if (!tabConfigEntry) {
                  return null;
                }
                return (
                  <RoleBasedRender key={tab} roles={tabConfigEntry.roles}>
                    <AiAppSecNavGroup
                      icon={tabConfigEntry.icon}
                      secureAiCodingTo={buildOrgPath(
                        organizationId,
                        OrgTab.SecureAiCoding
                      )}
                      threatModelingTo={buildOrgPath(
                        organizationId,
                        OrgTab.ThreatModeling
                      )}
                      vulnerabilityDiscoveryTo={buildOrgPath(
                        organizationId,
                        OrgTab.LlmVulnDiscovery
                      )}
                    />
                  </RoleBasedRender>
                );
              }

              const tabConfigEntry = tabConfig[tab as OrgTabType];
              // this is to escape eslint warning but should never happen due to isVisibleTab check
              if (!tabConfigEntry) {
                return null;
              }
              const { label, icon: Icon, roles } = tabConfigEntry;

              const isAgentsActivity = tab === OrgTab.AgentsActivity;
              const isShadowAI = tab === OrgTab.ShadowAI;
              const isPolicies = tab === OrgTab.Policies;

              return (
                <RoleBasedRender key={tab} roles={roles}>
                  {isAgentsActivity ? (
                    <AgentsActivityNavGroup
                      to={buildOrgPath(organizationId, tab)}
                      orgScoped
                    />
                  ) : isShadowAI ? (
                    <ShadowAINavGroup
                      to={buildOrgPath(organizationId, tab)}
                      icon={Icon}
                      showNetworkDiscovery={isNetworkDiscoveryEnabled}
                    />
                  ) : isPolicies ? (
                    <PoliciesNavGroup
                      to={buildOrgPath(organizationId, tab)}
                      icon={Icon}
                      showAiInstructions={isAiAgentInstructionsEnabled}
                    />
                  ) : (
                    <SidebarNavItem
                      to={buildOrgPath(organizationId, tab)}
                      icon={<Icon className="h-4 w-4 flex-shrink-0" />}
                      label={label}
                    />
                  )}
                </RoleBasedRender>
              );
            })}
          </SidebarNav>
        )}
      </SharedSidebar>

      <PageContentRoot>
        <PageHeader
          title={currentPageTitle}
          icon={currentPageIcon}
          subheader={currentPageSubheader}
        >
          {isUiV2Available && (
            <Button variant="outline" size="sm" onClick={() => setUiV2(true)}>
              Try the new UI
            </Button>
          )}
        </PageHeader>

        {/* Page Content */}
        <main className="flex-1 p-6 pb-20 w-full min-w-[320px]">{content}</main>
      </PageContentRoot>
    </>
  );
}
